import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { BrutalCard, BrutalButton } from "./Brutal";
import { ClipboardText, CaretDown, CaretUp, ArrowsClockwise } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

function Row({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-zinc-100 last:border-0">
      <span className="text-sm text-zinc-600">{label}</span>
      <span className="text-sm font-semibold font-mono text-zinc-950" data-testid={testId}>{value}</span>
    </div>
  );
}

export function MigrationReportCard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const load = () =>
    axios.get(`${API}/api/admin/migration-report`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData(null));

  useEffect(() => { load(); }, []);

  const runReconcile = async () => {
    if (!window.confirm(t("migration_report.reconcile_confirm"))) return;
    setReconciling(true);
    try {
      const r = await axios.post(`${API}/api/admin/reconcile-inventory`, {}, { withCredentials: true });
      const s = r.data;
      toast.success(t("migration_report.reconcile_done", {
        updated: (s.masters_updated || 0) + (s.children_updated || 0) + (s.children_created || 0),
        unchanged: s.unchanged || 0,
      }));
      await load();
    } catch {
      toast.error(t("migration_report.reconcile_failed"));
    } finally {
      setReconciling(false);
    }
  };

  if (!data) return null;
  const { report, live, reconcile } = data;
  const ranAt = report?.ran_at ? new Date(report.ran_at).toLocaleString() : "—";
  const purge = report?.user_purge;
  const purgeArchived = purge
    ? (purge.archived_with_history ?? 0) + (purge.archived_no_history ?? 0) + (purge.archived ?? 0)
    : 0;

  return (
    <BrutalCard className="p-5" data-testid="migration-report-card">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3"
        data-testid="migration-report-toggle"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <ClipboardText size={18} weight="bold" className="text-[#8a6a1c] shrink-0" />
          <div className="text-left min-w-0">
            <h2 className="font-semibold text-zinc-950 text-sm sm:text-base">{t("migration_report.title")}</h2>
            <p className="text-xs text-zinc-500 truncate">{t("migration_report.last_run")}: {ranAt}</p>
          </div>
        </div>
        {open ? <CaretUp size={16} weight="bold" /> : <CaretDown size={16} weight="bold" />}
      </button>

      {open && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8" data-testid="migration-report-body">
          <div>
            <p className="text-[11px] font-mono font-semibold tracking-[0.15em] text-[#8a6a1c] uppercase mb-1.5">
              {t("migration_report.last_startup")}
            </p>
            <Row label={t("migration_report.catalog_created")} value={report?.catalog?.created ?? 0} testId="mr-created" />
            <Row label={t("migration_report.catalog_repriced")} value={report?.catalog?.repriced ?? 0} testId="mr-repriced" />
            <Row label={t("migration_report.products_archived")} value={report?.products_archived ?? 0} testId="mr-archived" />
            <Row label={t("migration_report.repair_merged")} value={report?.repair?.merged ?? 0} testId="mr-merged" />
            <Row label={t("migration_report.repair_restored")} value={report?.repair?.restored ?? 0} testId="mr-restored" />
            {purge && <Row label={t("migration_report.users_purged")} value={purgeArchived + (purge.deleted ?? 0)} testId="mr-purged" />}
            {report?.katherine_seeded && <Row label={t("migration_report.katherine")} value="✓" testId="mr-katherine" />}
          </div>
          <div>
            <p className="text-[11px] font-mono font-semibold tracking-[0.15em] text-[#8a6a1c] uppercase mb-1.5 mt-4 sm:mt-0">
              {t("migration_report.live_counts")}
            </p>
            <Row label={t("migration_report.active_products")} value={live.active_products} testId="mr-live-products" />
            {live.held_child_rows != null && (
              <Row label={t("migration_report.held_child_rows")} value={live.held_child_rows} testId="mr-live-held" />
            )}
            <Row label={t("migration_report.archived_products")} value={live.archived_products} testId="mr-live-archived" />
            <Row label={t("migration_report.active_users")} value={live.active_users} testId="mr-live-users" />
            <Row label={t("migration_report.movement_logs")} value={live.movement_logs} testId="mr-live-logs" />
            <Row label={t("migration_report.settlements")} value={live.settlements} testId="mr-live-settlements" />
            <Row label={t("migration_report.expenses")} value={live.expenses} testId="mr-live-expenses" />
          </div>

          {/* Reconciliation section */}
          <div className="sm:col-span-2 mt-4 pt-4 border-t border-zinc-200">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-[11px] font-mono font-semibold tracking-[0.15em] text-[#8a6a1c] uppercase">
                  {t("migration_report.reconcile_title")}
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {reconcile?.ran_at
                    ? t("migration_report.reconcile_last", { at: new Date(reconcile.ran_at).toLocaleString() })
                    : t("migration_report.reconcile_never")}
                </p>
              </div>
              <BrutalButton size="sm" onClick={runReconcile} disabled={reconciling} data-testid="mr-reconcile-btn">
                <ArrowsClockwise size={14} weight="bold" className={reconciling ? "animate-spin" : ""} />
                {reconciling ? t("migration_report.reconciling") : t("migration_report.reconcile_run")}
              </BrutalButton>
            </div>
            {reconcile && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 mt-2" data-testid="mr-reconcile-stats">
                <Row label={t("migration_report.reconcile_updated")}
                     value={(reconcile.masters_updated ?? 0) + (reconcile.children_updated ?? 0) + (reconcile.children_created ?? 0)}
                     testId="mr-rec-updated" />
                <Row label={t("migration_report.reconcile_unchanged")} value={reconcile.unchanged ?? 0} testId="mr-rec-unchanged" />
                <Row label={t("migration_report.reconcile_units_root")} value={reconcile.units_root ?? 0} testId="mr-rec-units" />
                <Row label={t("migration_report.reconcile_unmapped")} value={reconcile.unmapped_count ?? 0} testId="mr-rec-unmapped" />
              </div>
            )}
            {reconcile?.changes?.length > 0 && (
              <div className="mt-3 max-h-48 overflow-y-auto border border-zinc-100 rounded-lg" data-testid="mr-reconcile-changes">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-zinc-50">
                    <tr className="text-left font-mono uppercase tracking-wider text-zinc-400">
                      <th className="px-3 py-1.5 font-medium">{t("migration_report.reconcile_holder")}</th>
                      <th className="px-3 py-1.5 font-medium">{t("migration_report.reconcile_product")}</th>
                      <th className="px-3 py-1.5 font-medium text-right">{t("migration_report.reconcile_before")}</th>
                      <th className="px-3 py-1.5 font-medium text-right">{t("migration_report.reconcile_after")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reconcile.changes.map((c, i) => (
                      <tr key={i} className="border-t border-zinc-50">
                        <td className="px-3 py-1.5 text-zinc-600">{c.owner}</td>
                        <td className="px-3 py-1.5 font-medium text-zinc-900">{c.product}</td>
                        <td className="px-3 py-1.5 text-right font-mono text-zinc-500">{c.before}</td>
                        <td className="px-3 py-1.5 text-right font-mono font-semibold text-[#8a6a1c]">{c.after}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {reconcile?.unmapped?.length > 0 && (
              <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="mr-reconcile-unmapped-list">
                {t("migration_report.reconcile_unmapped_hint")}{" "}
                {reconcile.unmapped.map((u) => `${u.product} (${u.qty})`).join(", ")}
              </p>
            )}
          </div>
        </div>
      )}
    </BrutalCard>
  );
}
